/* Hifz: today's new lesson (sabaq) first and biggest, then the revision that
   keeps it — recent pages (sabqi) and older pages on a spaced schedule (manzil).
   The planning and scheduling are in ./hifz.ts. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQueries } from '@tanstack/react-query';
import { AlertTriangle, BookOpenText, Check, Palette, CheckCircle2, ChevronRight, Eye, EyeOff, Headphones, Minus, Pause, Plus, SlidersHorizontal } from 'lucide-react';
import { dayKeyIn } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import { Card, IconButton, LargeTitle, Ring, Sheet, Stepper, haptic } from '@/components/ui';
import type { RepeatSettings } from '@/lib/types';
import { StarIcon } from '@/components/islamic/Ornaments';
import { CHAPTERS, JUZS, chapter, cleanTranslation, verseId, versesByChapter, type Verse } from '@/features/quran/api';
import { indopakVerse, qpcVerse } from '@/features/quran/script';
import { TajweedLegend, TajweedText } from '@/features/quran/Tajweed';
import { alignTajweed } from '@/features/quran/tajweedAlign';
import { usePlayer } from '@/features/player/audioEngine';
import { HadithCard, VIRTUES_CREDIT } from '@/features/adhkar/Virtues';
import { AyatCard } from '@/features/adhkar/Sins';
import virtues from '@/data/virtues.json';
import {
  GOAL_YEARS, PAGE_COUNT, dailyPages, isRecent, isStudyDay, juzProgress, linesOf, nextLesson, needsWork, paceStatus, pageOfId, pageParts,
  pagesMemorised, keyOfId, passageOfId, weightOf, planSequence, reviewQueue, targetFor, PASSAGES, type Grade, type HifzOrder, type HifzLessonRec, type HifzPage, type HifzPlan, type Lesson,
} from './hifz';

const HIFZ = (virtues as unknown as { hifz: { virtue: string[]; keep: string[]; ayat: string[]; passages: Record<string, string[]> } }).hifz;

/** "Āyat al-Kursī — the greatest āyah": shown when a lesson is one of the Sunnah passages. */
function PassageBadge({ lesson, plan }: { lesson: Lesson; plan: HifzPlan }) {
  const ids = planSequence(plan).slice(lesson.from, lesson.to);
  const ps = [...new Set(ids.map(passageOfId).filter((p): p is NonNullable<typeof p> => !!p))];
  if (!ps.length) return null;
  return (
    <div className="mb-3 rounded-2xl bg-gold/15 px-3 py-2 text-[13px]" data-testid="hifz-passage">
      {ps.map((p) => <div key={p.id}><b className="text-gold">{p.title}</b> <span className="text-muted">— {p.why}</span></div>)}
    </div>
  );
}
const useToday = () => dayKeyIn(useSettings((s) => s.settings.prayer.tz));
const NO_PAGES: Record<string, HifzPage> = {};
const NO_LESSONS: Record<string, HifzLessonRec> = {};
const fmtDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const partsLabel = (parts: Lesson['parts']) => parts.map((p) => `${chapter(p.surah).n} ${p.from === p.to ? p.from : `${p.from}–${p.to}`}`).join(' · ');
const JUZ_FIRST = JUZS.map((j) => verseId(j.first));
const fmtPages = (n: number) => (n >= 10 ? Math.round(n).toString() : (Math.round(n * 10) / 10).toString());

function usePlan(): HifzPlan | null {
  const p = useProgress((s) => s.hifz?.main);
  return p && !p.deleted ? p : null;
}

/** Today's lesson. Its size comes from where you were when the day began, so it holds still as you learn;
    once it's learnt, `next` is the one after (for a day you want to do more). */
function useLesson(plan: HifzPlan | null, today: string) {
  const learntToday = useProgress((s) => s.hifzLog?.[today]?.newAyat ?? 0);
  return useMemo(() => {
    if (!plan) return null;
    const startPos = Math.max(0, plan.position - learntToday);
    const size = dailyPages(plan, today, startPos);
    const next = nextLesson(plan, size);
    return { size, next, doneToday: learntToday > 0, studyDay: isStudyDay(plan, today), learntToday };
  }, [plan, today, learntToday]);
}

/* ── Home ─────────────────────────────────────────────────────────── */
export function HifzHome() {
  const plan = usePlan();
  const today = useToday();
  const [setup, setSetup] = useState(false);
  return plan ? <HifzToday plan={plan} today={today} onEdit={() => setSetup(true)} setup={<SetupSheet open={setup} onClose={() => setSetup(false)} plan={plan} />} />
    : <HifzIntro onStart={() => setSetup(true)} setup={<SetupSheet open={setup} onClose={() => setSetup(false)} plan={null} />} />;
}

function HifzIntro({ onStart, setup }: { onStart: () => void; setup: ReactNode }) {
  return (
    <div className="page-pad">
      <LargeTitle back="/quran" title="Hifz" ar="الحفظ" subtitle="Memorise the Qurʾān" />
      <div className="space-y-4 px-4 pb-8">
        <Card className="p-5 text-center">
          <StarIcon size={44} className="mx-auto text-gold" />
          <div className="mt-2 text-[19px] font-bold">Carry the Qurʾān in your heart</div>
          <p className="mt-1 text-[14px] leading-relaxed text-muted">Set when you want to finish — one year, two, three or more — and every day you get a new lesson sized to get you there, and the revision that keeps what you’ve learnt.</p>
          <button type="button" onClick={onStart} className="press mt-4 w-full rounded-2xl bg-accent py-3 text-[16px] font-semibold text-accent-ink">Make my plan</button>
          <Link to="/hifz/guide" className="mt-3 block text-[14px] font-semibold text-accent">How to memorise · the methods</Link>
        </Card>
        <Tracks />
        <HadithCard id={HIFZ.virtue[1]} />
      </div>
      {setup}
    </div>
  );
}

/** The three daily tracks, in a line each. */
function Tracks() {
  return (
    <Card className="space-y-3 p-4">
      {([['Sabaq · new lesson', 'Today’s new āyāt — the most important part of the day.'], ['Sabqi · recent', 'Every page you learn is revised every day for its first week.'], ['Manzil · older', 'Then on a spaced schedule: pages you know well come back less often (at least monthly), weak ones sooner.']] as const).map(([t, d], i) => (
        <div key={t} className="flex gap-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gold/15 text-[13px] font-bold text-gold">{i + 1}</span>
          <div><div className="text-[15px] font-semibold">{t}</div><div className="text-[13px] leading-snug text-muted">{d}</div></div>
        </div>
      ))}
    </Card>
  );
}

function HifzToday({ plan, today, onEdit, setup }: { plan: HifzPlan; today: string; onEdit: () => void; setup: ReactNode }) {
  const pages = useProgress((s) => s.hifzPages ?? NO_PAGES);
  const lesson = useLesson(plan, today)!;
  const q = useMemo(() => reviewQueue(pages, today, plan.maxReview), [pages, today, plan.maxReview]);
  const memorised = useMemo(() => pagesMemorised(plan), [plan]);
  const juz = useMemo(() => juzProgress(plan, JUZ_FIRST), [plan]);
  const pace = useMemo(() => paceStatus(plan, today), [plan, today]);
  const weak = useMemo(() => Object.values(pages).filter((p) => needsWork(p) && !q.recent.includes(p) && !q.due.includes(p)).sort((a, b) => a.ease - b.ease).slice(0, 8), [pages, q]);
  const finished = !lesson.next && planSequence(plan).length <= plan.position;
  const toRevise = q.recent.length + q.due.length;

  return (
    <div className="page-pad">
      <LargeTitle back="/quran" title="Hifz" ar="الحفظ" subtitle="Memorise · revise · keep"
        right={<><IconButton label="How to memorise" to="/hifz/guide"><BookOpenText size={18} /></IconButton><IconButton label="Plan settings" onClick={onEdit}><SlidersHorizontal size={18} /></IconButton></>} />

      <div className="space-y-4 px-4 pb-8">
        {/* 1 · the new lesson — what needs memorising today, first and biggest */}
        <section aria-label="Today’s new lesson">
          <div className="flex items-center gap-1.5 px-1 pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted"><StarIcon size={11} strokeWidth={2} className="text-gold" />Sabaq · today’s new lesson</div>
          {finished ? (
            <Card className="bg-gradient-to-br from-gold/25 to-transparent p-5 text-center">
              <div className="font-kufi text-[26px] text-gold" dir="rtl">الحمد لله</div>
              <p className="mt-1 text-[16px] font-semibold">You’ve memorised the whole Qurʾān.</p>
              <p className="mt-1 text-[14px] text-muted">Now keep it: your revision carries on every day below.</p>
            </Card>
          ) : lesson.doneToday ? (
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={30} className="shrink-0 text-[var(--win-ok)]" />
                <div className="min-w-0 flex-1">
                  <div className="text-[17px] font-bold">Today’s lesson is memorised</div>
                  <div className="text-[13px] text-muted">Mā shāʾ Allāh. Recite it in your prayers today — then revise below.</div>
                </div>
              </div>
              {lesson.next && <Link to="/hifz/lesson" className="press mt-3 flex items-center justify-between rounded-xl bg-fill px-3 py-2.5 text-[14px] font-semibold"><span className="truncate">Learn more: {partsLabel(lesson.next.parts)}</span><ChevronRight size={16} /></Link>}
              <UndoToday today={today} />
            </Card>
          ) : lesson.next && (
            <Link to="/hifz/lesson" className="press block overflow-hidden rounded-[24px] bg-gradient-to-br from-accent/30 via-accent/10 to-transparent p-5 shadow-[inset_0_0_0_1px_var(--card-edge)]" data-testid="hifz-lesson">
              {!lesson.studyDay && <div className="mb-2 rounded-full bg-fill px-3 py-1 text-center text-[12px] font-semibold text-muted">A revision day in your plan — learn this only if you have time</div>}
              {lesson.next.relearn && <div className="mb-2 rounded-full bg-[var(--win-warn)]/15 px-3 py-1 text-center text-[12px] font-semibold text-[var(--win-warn)]">Learning again — you marked this not memorised</div>}
              <PassageBadge lesson={lesson.next} plan={plan} />

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[24px] font-bold leading-tight">{partsLabel(lesson.next.parts)}</div>
                  <div className="mt-1 text-[14px] text-muted">≈ {linesOf(lesson.next.pages)} lines · page {[...new Set(lesson.next.parts.map((p) => pageOfId(verseId(`${p.surah}:${p.from}`))))].join(', ')}</div>
                </div>
                <span className="font-arabic text-[30px] leading-none text-gold" dir="rtl">{chapter(lesson.next.parts[0].surah).ar}</span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-accent py-3 text-[16px] font-semibold text-accent-ink">Start today’s lesson <ChevronRight size={18} /></div>
            </Link>
          )}
        </section>

        {/* 2 · revision */}
        <section aria-label="Revision">
          <div className="flex items-center justify-between px-1 pb-2">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-muted"><StarIcon size={11} strokeWidth={2} className="text-gold" />Revision today</span>
            <span className="text-[13px] font-semibold tabular-nums text-muted">{q.done.length} done{toRevise ? ` · ${toRevise} to go` : ''}</span>
          </div>
          {toRevise === 0 ? (
            <Card className="p-4 text-center text-[14px] text-muted">{q.done.length ? 'All revised for today — jazāk Allāhu khayran.' : 'Nothing to revise yet. Pages you learn come back here every day for a week, then on a schedule.'}</Card>
          ) : (
            <div className="space-y-3">
              {q.recent.length > 0 && <PageGroup title="Sabqi · learnt this week" note="Every day for a week" pages={q.recent} plan={plan} today={today} />}
              {q.due.length > 0 && <PageGroup title="Manzil · older pages" note={q.waiting ? `${q.waiting} more wait for tomorrow` : 'Due today'} pages={q.due} plan={plan} today={today} />}
            </div>
          )}
        </section>

        <SavedLessons today={today} />

        {weak.length > 0 && (
          <section aria-label="Needs strengthening">
            <div className="flex items-center gap-1.5 px-1 pb-2 text-[13px] font-semibold uppercase tracking-wide text-[var(--win-warn)]"><AlertTriangle size={13} />Needs strengthening</div>
            <PageGroup title="Revise these extra if you have time" pages={weak} plan={plan} today={today} />
          </section>
        )}

        {/* 3 · progress */}
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <Ring value={memorised / PAGE_COUNT} size={76} stroke={8}><span className="text-[15px] font-bold tabular-nums">{(() => { const pct = (memorised / PAGE_COUNT) * 100; return pct > 0 && pct < 10 ? pct.toFixed(1) : Math.round(pct); })()}%</span></Ring>
            <div className="min-w-0 flex-1">
              <div className="text-[17px] font-bold tabular-nums">{fmtPages(memorised)} of {PAGE_COUNT} pages</div>
              <div className="text-[13px] text-muted">{juz.filter((j) => j > 0.999).length} juz complete · ≈ {linesOf(lesson.size)} lines a lesson</div>
              {plan.targetDay ? (
                <div className={`mt-1 text-[13px] font-semibold ${pace.onTrack ? 'text-[var(--win-ok)]' : 'text-[var(--win-warn)]'}`}>
                  {pace.onTrack ? `On track to finish by ${fmtDate(plan.targetDay)}` : `${pace.behindDays} study ${pace.behindDays === 1 ? 'day' : 'days'} behind · finish by ${fmtDate(plan.targetDay)}`}
                </div>
              ) : <div className="mt-1 text-[13px] text-muted">At your own pace</div>}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-10 gap-1" aria-label="Memorised by juz">
            {juz.map((v, i) => (
              <div key={i} title={`Juz ${i + 1}: ${Math.round(v * 100)}%`} className="relative grid aspect-square place-items-center overflow-hidden rounded-md bg-fill text-[10px] font-semibold tabular-nums">
                <span className="absolute inset-x-0 bottom-0 bg-accent/70" style={{ height: `${v * 100}%` }} />
                <span className="relative">{i + 1}</span>
              </div>
            ))}
          </div>
        </Card>

        <Link to="/hifz/guide" className="press flex items-center gap-3 rounded-[20px] bg-surface p-4">
          <BookOpenText size={20} className="shrink-0 text-gold" />
          <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold">How to memorise</span><span className="block text-[13px] text-muted">The methods ḥuffāẓ use, and the hadith on keeping the Qurʾān</span></span>
          <ChevronRight size={18} className="text-muted" />
        </Link>
      </div>
      {setup}
    </div>
  );
}

/** One saved lesson, as a label. */
const recLabel = (ids: number[]) => partsLabel(partsOfKeys(ids.map(keyOfId)));
const recDay = (day: string, today: string) => (day === today ? 'Today' : new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }));

/** "Not memorised yet? Undo" for the last lesson saved today. */
function UndoToday({ today }: { today: string }) {
  const lessons = useProgress((s) => s.hifzLessons ?? NO_LESSONS);
  const unlearn = useProgress((s) => s.unlearnHifzLesson);
  const last = Object.values(lessons).filter((r) => !r.deleted && r.day === today).sort((a, b) => b.updatedAt - a.updatedAt)[0];
  if (!last) return null;
  return (
    <button type="button" onClick={() => { if (confirm(`Mark ${recLabel(last.ids)} as not memorised? It goes back to be learnt again.`)) { haptic(20); unlearn(last.id); } }}
      className="mt-2 w-full text-center text-[13px] font-semibold text-muted underline-offset-2 hover:underline">Saved too soon? Mark it not memorised</button>
  );
}

/** Lessons you've saved, newest first — any can be marked "not memorised" if it was saved too soon. */
function SavedLessons({ today }: { today: string }) {
  const lessons = useProgress((s) => s.hifzLessons ?? NO_LESSONS);
  const unlearn = useProgress((s) => s.unlearnHifzLesson);
  const [all, setAll] = useState(false);
  const list = useMemo(() => Object.values(lessons).filter((r) => !r.deleted).sort((a, b) => b.updatedAt - a.updatedAt), [lessons]);
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 5);
  return (
    <section aria-label="Saved lessons">
      <div className="flex items-center gap-1.5 px-1 pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted"><StarIcon size={11} strokeWidth={2} className="text-gold" />Saved lessons</div>
      <div className="overflow-hidden rounded-[20px] bg-surface" data-testid="hifz-saved">
        {shown.map((r) => (
          <div key={r.id} className="flex items-center gap-3 border-b border-hairline px-4 py-3 last:border-0" data-lesson={r.id}>
            <CheckCircle2 size={18} className="shrink-0 text-[var(--win-ok)]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold">{recLabel(r.ids)}</span>
              <span className="block text-[12px] text-muted">{recDay(r.day, today)} · {r.ids.length} {r.ids.length === 1 ? 'āyah' : 'āyāt'}</span>
            </span>
            <button type="button" onClick={() => { if (confirm(`Mark ${recLabel(r.ids)} as not memorised? It goes back to be learnt again — before any new lesson.`)) { haptic(20); unlearn(r.id); } }}
              className="press shrink-0 rounded-full bg-fill px-3 py-1.5 text-[12px] font-semibold text-muted" aria-label={`Not memorised: ${recLabel(r.ids)}`}>Not memorised</button>
          </div>
        ))}
        {list.length > 5 && <button type="button" onClick={() => setAll((v) => !v)} className="w-full py-2.5 text-center text-[13px] font-semibold text-accent">{all ? 'Show fewer' : `Show all ${list.length}`}</button>}
      </div>
    </section>
  );
}

function PageGroup({ title, note, pages, plan, today }: { title: string; note?: string; pages: HifzPage[]; plan: HifzPlan; today: string }) {
  return (
    <div className="overflow-hidden rounded-[20px] bg-surface">
      <div className="flex items-baseline justify-between border-b border-hairline px-4 py-2">
        <span className="text-[14px] font-semibold">{title}</span>
        {note && <span className="text-[12px] text-muted">{note}</span>}
      </div>
      {pages.map((p) => {
        const parts = pageParts(plan, p.page);
        return (
          <Link key={p.id} to={`/hifz/review/${p.page}`} className="press flex items-center gap-3 border-b border-hairline px-4 py-3 last:border-0">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-fill text-[14px] font-bold tabular-nums">{p.page}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold">{parts.length ? partsLabel(parts) : `Page ${p.page}`}</span>
              <span className="block text-[12px] text-muted">{isRecent(p, today) ? 'Learnt this week' : p.last ? `Last: ${p.last.grade === 'strong' ? 'strong' : p.last.grade === 'shaky' ? 'some mistakes' : 'forgot'}` : 'First revision'}{p.lapses ? ` · forgotten ${p.lapses}×` : ''}</span>
            </span>
            <ChevronRight size={16} className="text-muted" />
          </Link>
        );
      })}
    </div>
  );
}

/* ── Āyāt, with a "test yourself" mode ───────────────────────────────── */
function useParts(parts: Lesson['parts']) {
  const translationId = useSettings((s) => s.settings.translationId);
  const surahs = [...new Set(parts.map((p) => p.surah))];
  const qs = useQueries({ queries: surahs.map((s) => ({ queryKey: ['surah', s, translationId], queryFn: () => versesByChapter(s, translationId), staleTime: Infinity })) });
  const loading = qs.some((q) => q.isLoading), error = qs.some((q) => q.error);
  const verses: Verse[] = [];
  for (const p of parts) {
    const data = qs[surahs.indexOf(p.surah)]?.data;
    if (data) verses.push(...data.filter((v) => v.verse_number >= p.from && v.verse_number <= p.to));
  }
  return { verses, loading, error };
}

/** The āyāt. Hidden: each shows its first word as a prompt, the rest covered until you tap it. */
/** Āyāt ticked off as memorised in a lesson. */
type Marks = { done: ReadonlySet<string>; toggle: (key: string) => void };

function AyahList({ parts, hidden, marks }: { parts: Lesson['parts']; hidden: boolean; marks?: Marks }) {
  const { verses, loading, error } = useParts(parts);
  const { mushaf, arabicSize, showTranslation, hifzTajweed } = useSettings((s) => s.settings);
  const tajweed = hifzTajweed !== false && mushaf !== 'indopak';   // the colours follow the King Fahd text, not the IndoPak script
  const [shown, setShown] = useState<Set<string>>(new Set());
  if (loading) return <p className="py-10 text-center text-muted">Loading…</p>;
  if (error && !verses.length) return <p className="py-10 text-center text-muted">Couldn’t load these āyāt — check your connection.</p>;
  return (
    <div className="space-y-3">
      {verses.map((v) => {
        const text = mushaf === 'indopak' ? indopakVerse(v) ?? qpcVerse(v) : qpcVerse(v);
        const cover = hidden && !shown.has(v.verse_key);
        const [first, ...rest] = text.split(' ');
        const done = marks?.done.has(v.verse_key);
        return (
          <div key={v.verse_key} className={`rounded-2xl bg-surface ${done ? 'shadow-[inset_0_0_0_1.5px_var(--win-ok)]' : ''}`}>
            <button type="button" onClick={() => hidden && setShown((s) => { const n = new Set(s); if (n.has(v.verse_key)) n.delete(v.verse_key); else n.add(v.verse_key); return n; })}
              className="block w-full px-4 py-3 text-right" aria-label={cover ? `Reveal ${v.verse_key}` : v.verse_key} data-verse={v.verse_key}>
              <p className={`ayah-text ${mushaf === 'indopak' ? 'indopak' : 'uthmani'}`} dir="rtl" style={{ fontSize: `${1.75 * arabicSize}rem`, lineHeight: 2.1 }}>
                {cover ? <>{first} <span className="select-none blur-[9px]" aria-hidden>{rest.join(' ')}</span></>
                  : tajweed && v.text_uthmani_tajweed ? <TajweedText html={alignTajweed(qpcVerse(v), v.text_uthmani_tajweed, false)} /> : text}
                <span className="ayah-num"> ﴿{v.verse_number.toLocaleString('ar-EG')}﴾</span>
              </p>
              {!cover && showTranslation && v.translations?.[0] && <p className="mt-2 text-left text-[14px] leading-relaxed text-muted" dir="ltr">{cleanTranslation(v.translations[0].text)}</p>}
            </button>
            {marks && (
              <div className="flex justify-start px-3 pb-3">
                <button type="button" aria-pressed={!!done} aria-label={`Memorised ${v.verse_key}`} onClick={() => { haptic(done ? 8 : 25); marks.toggle(v.verse_key); }}
                  className={`press flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${done ? 'bg-[var(--win-ok)]/15 text-[var(--win-ok)]' : 'bg-fill text-muted'}`}>
                  {done ? <CheckCircle2 size={15} /> : <Check size={15} />}{done ? 'Memorised' : 'Mark memorised'}
                </button>
              </div>
            )}
          </div>
        );
      })}
      {hidden && <p className="text-center text-[12px] text-muted">Recite each āyah from memory, then tap it to check.</p>}
    </div>
  );
}

/* Listening here sets the player to repeat each āyah with a pause; your own repeat settings
   are put back (and the lesson audio stopped) when you leave the page. They're kept on the device
   too, so closing the app mid-lesson can't leave your normal recitation repeating (restored at start). */
const SAVED_KEY = 'ftu_hifz_saved_repeat';
function saveRepeat() {
  try { if (localStorage.getItem(SAVED_KEY)) return; localStorage.setItem(SAVED_KEY, JSON.stringify(useSettings.getState().settings.repeat)); } catch {}
}
/** Put your own player repeat settings back, if a hifz lesson changed them. Safe to call any time. */
export function restoreHifzRepeat() {
  let saved: RepeatSettings | null = null;
  try { saved = JSON.parse(localStorage.getItem(SAVED_KEY) || 'null'); localStorage.removeItem(SAVED_KEY); } catch {}
  if (!saved) return;
  const st = usePlayer.getState();
  if (st.status === 'playing' || st.status === 'gap') st.pause();
  useSettings.getState().updateDeep('repeat', saved);
}
function useRestoreRepeat() {
  useEffect(() => () => restoreHifzRepeat(), []);
}

/** Listen with each āyah repeated and a pause to repeat it after the reciter. */
function ListenButton({ part, label = 'Listen & repeat' }: { part: Lesson['parts'][number]; label?: string }) {
  const player = usePlayer();
  const updateDeep = useSettings((s) => s.updateDeep);
  const on = player.surah === part.surah && player.range?.from === part.from && (player.status === 'playing' || player.status === 'gap');
  return (
    <button type="button" onClick={() => {
      if (on) { player.pause(); return; }
      saveRepeat();
      updateDeep('repeat', { ayahRepeat: 3, rangeRepeat: 0, gapMode: 'x1' });   // each āyah three times, with time to repeat it, the lesson on a loop
      void player.play(part.surah, { fromAyah: part.from, toAyah: part.to });
    }} className="press flex items-center gap-2 rounded-full bg-fill px-4 py-2 text-[14px] font-semibold">
      {on ? <Pause size={16} /> : <Headphones size={16} />}{on ? 'Stop' : label}
    </button>
  );
}

/* ── The lesson ───────────────────────────────────────────────────── */
const STEPS: [string, string][] = [
  ['Listen', 'Hear it from a good reciter several times — each āyah is repeated with a pause for you to say it after him.'],
  ['Read it looking', 'Read the whole lesson slowly and correctly, ten times or more, with its tajweed — the colours show each rule as you read.'],
  ['One āyah at a time', 'Recite the first āyah from memory until you can say it three times without a mistake.'],
  ['Link', 'Add the next āyah, then recite both together. Keep adding and joining until the lesson is one piece.'],
  ['Test yourself', 'Switch on “Test myself” and recite it all from memory. Recite it to someone if you can.'],
  ['Keep it', 'Recite it in your prayers today — and in tomorrow’s revision.'],
];

export function HifzLesson() {
  const plan = usePlan();
  const today = useToday();
  const nav = useNavigate();
  const lesson = useLesson(plan, today);
  const learn = useProgress((s) => s.learnHifzLesson);
  const [hidden, setHidden] = useState(false);
  const [reps, setReps] = useState(0);
  const [steps, setSteps] = useState(true);
  const [guided, setGuided] = useState(false);
  const [marked, setMarked] = useState<Set<string>>(new Set());
  useRestoreRepeat();
  const l = lesson?.next;
  if (!plan || !l) return <div className="page-pad px-6 pt-20 text-center"><p className="text-muted">No lesson to learn.</p><Link to="/hifz" className="font-semibold text-accent">Back to Hifz</Link></div>;
  const ids = planSequence(plan).slice(l.from, l.to);
  const keys = ids.map(keyOfId);
  const marks: Marks = { done: marked, toggle: (k) => setMarked((m) => { const n = new Set(m); if (n.has(k)) n.delete(k); else n.add(k); return n; }) };
  const markOn = (k: string) => setMarked((m) => (m.has(k) ? m : new Set(m).add(k)));
  /* What the button saves: the whole lesson, or — when only some āyāt are ticked — the ticked ones from the
     start (memorising runs in order; the rest begins your next lesson). */
  let prefix = 0;
  while (prefix < keys.length && marked.has(keys[prefix])) prefix++;
  const partial = marked.size > 0 && prefix < keys.length;
  const save = () => {
    haptic(60);
    if (partial) {
      if (!prefix) return;
      const part = keys.slice(0, prefix);
      learn({ from: l.from, to: l.from + prefix, parts: partsOfKeys(part), pages: ids.slice(0, prefix).reduce((a, id) => a + weightOf(id), 0), relearn: l.relearn }, ids.slice(0, prefix), today);
    } else learn(l, ids, today);
    nav('/hifz');
  };

  return (
    <div className="page-pad">
      <LargeTitle back="/hifz" title={lesson.doneToday ? 'Next lesson' : 'Today’s lesson'} subtitle={`${partsLabel(l.parts)} · ≈ ${linesOf(l.pages)} lines`} />
      <div className="space-y-4 px-4 pb-40">
        <Card className="p-4">
          <button type="button" onClick={() => setSteps((v) => !v)} className="flex w-full items-center justify-between text-left">
            <span className="text-[15px] font-semibold">How to learn it</span><span className="text-[13px] font-semibold text-accent">{steps ? 'Hide' : 'Show'}</span>
          </button>
          {steps && (
            <ol className="mt-3 space-y-2.5">
              {STEPS.map(([t, d], i) => (
                <li key={t} className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gold/15 text-[12px] font-bold text-gold">{i + 1}</span><span className="text-[14px] leading-snug"><b>{t}.</b> <span className="text-muted">{d}</span></span></li>
              ))}
            </ol>
          )}
          {!guided && (
            <button type="button" onClick={() => { haptic(20); setGuided(true); }} className="press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-accent/15 py-3 text-[15px] font-semibold text-accent" data-testid="hifz-step-start">
              <Headphones size={18} /> Listen & recite, step by step
            </button>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!guided && l.parts.map((p) => <ListenButton key={`${p.surah}:${p.from}`} part={p} label={l.parts.length > 1 ? `Listen · ${chapter(p.surah).n}` : 'Listen to it all'} />)}
            <span className="ml-auto flex items-center gap-1 rounded-full bg-fill px-1.5 py-1" aria-label="Times recited">
              <button type="button" aria-label="One fewer" onClick={() => setReps((n) => Math.max(0, n - 1))} className="press grid h-7 w-7 place-items-center rounded-full"><Minus size={14} /></button>
              <button type="button" onClick={() => { haptic(8); setReps((n) => n + 1); }} className="press min-w-[64px] text-center text-[14px] font-bold tabular-nums">{reps}× read</button>
              <button type="button" aria-label="One more" onClick={() => { haptic(8); setReps((n) => n + 1); }} className="press grid h-7 w-7 place-items-center rounded-full"><Plus size={14} /></button>
            </span>
          </div>
        </Card>

        {l.relearn && <div className="rounded-2xl bg-[var(--win-warn)]/15 px-3 py-2 text-[13px] font-semibold text-[var(--win-warn)]">Learning again — you marked this lesson not memorised. New lessons carry on after it.</div>}
        <PassageBadge lesson={l} plan={plan} />
        {guided ? <StepByStep lesson={l} marks={marks} onLearnt={markOn} onExit={() => setGuided(false)} /> : <>
          <HideToggle hidden={hidden} onChange={setHidden} />
          <AyahList parts={l.parts} hidden={hidden} marks={marks} />
        </>}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[calc(var(--safe-bottom)+76px)] pt-2">
        <div className="mx-auto max-w-[680px]">
          {marked.size > 0 && <div className="mb-1.5 text-center text-[12px] font-semibold tabular-nums text-muted" data-testid="hifz-marked">{marked.size} of {keys.length} āyāt memorised{partial && !prefix ? ' — start from the first āyah' : ''}</div>}
          <button type="button" onClick={save} disabled={partial && !prefix} className="press flex w-full items-center justify-center gap-2 rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink shadow-lg disabled:opacity-40">
            <Check size={20} /> {partial && prefix ? `Save ${prefix} memorised ${prefix === 1 ? 'āyah' : 'āyāt'} — the rest tomorrow` : 'I’ve memorised it'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Step by step: listen to one āyah, recite it from memory, add the next, recite the chain ──
   The classic way of linking a lesson together: āyah 1 is heard (each time with a pause to repeat it),
   then recited from memory; then āyah 2 is heard and āyāt 1–2 are recited together; and so on, until
   the whole lesson is one piece. An āyah you struggle with gets more listens (remembered for it). */
const OWN_KEY = 'ftu_hifz_listens';
const readOwn = (): Record<string, number> => { try { return JSON.parse(localStorage.getItem(OWN_KEY) || '{}'); } catch { return {}; } };
const writeOwn = (v: Record<string, number>) => { try { localStorage.setItem(OWN_KEY, JSON.stringify(v)); } catch {} };

/** Verse keys → { surah, from, to } runs, for AyahList. */
function partsOfKeys(keys: string[]): Lesson['parts'] {
  const out: Lesson['parts'] = [];
  for (const k of keys) {
    const [s, a] = k.split(':').map(Number), last = out.at(-1);
    if (last && last.surah === s && last.to === a - 1) last.to = a;
    else out.push({ surah: s, from: a, to: a });
  }
  return out;
}

function StepByStep({ lesson, marks, onLearnt, onExit }: { lesson: Lesson; marks: Marks; onLearnt: (key: string) => void; onExit: () => void }) {
  const keys = useMemo(() => lesson.parts.flatMap((p) => Array.from({ length: p.to - p.from + 1 }, (_, i) => `${p.surah}:${p.from + i}`)), [lesson]);
  const base = useSettings((s) => s.settings.hifzListens ?? 3);
  const chainListens = useSettings((s) => s.settings.hifzChainListens ?? 3);
  const update = useSettings((s) => s.update);
  const updateDeep = useSettings((s) => s.updateDeep);
  const player = usePlayer();
  const [own, setOwn] = useState<Record<string, number>>(readOwn);
  const [k, setK] = useState(0);
  const [phase, setPhase] = useState<StepPhase>('listen');
  /** what the player is playing for us: one āyah's listens, or the chain (one surah-run at a time, `passes` times over) */
  const waiting = useRef<{ kind: 'ayah'; key: string; done: number } | { kind: 'chain'; segs: Lesson['parts']; i: number; pass: number; passes: number; done: number } | null>(null);
  const [chainPass, setChainPass] = useState(0);
  const [left, setLeft] = useState(0);      // seconds of "your turn" left
  const [total, setTotal] = useState(1);
  const key = keys[k];
  const [surah, ayah] = key.split(':').map(Number);
  const listens = own[key] ?? base;
  const chain = keys.slice(0, k + 1);
  const label = (ks: string[]) => partsLabel(partsOfKeys(ks));
  const busy = player.status === 'playing' || player.status === 'gap' || player.status === 'loading';
  const hearing = busy && player.surah === surah && player.range?.from === ayah && player.range?.to === ayah;

  /** Hear an āyah `n` times, each with a pause to repeat it after the reciter. */
  const listen = (at: string, n: number) => {
    const [s, a] = at.split(':').map(Number);
    saveRepeat();
    updateDeep('repeat', { ayahRepeat: n, rangeRepeat: 1, gapMode: 'x1' });
    waiting.current = { kind: 'ayah', key: at, done: usePlayer.getState().rangeDone };
    setPhase('listen');
    void usePlayer.getState().play(s, { fromAyah: a, toAyah: a });
  };
  /** Hear the chain so far recited straight through, `passes` times (a lesson across surahs plays each surah's part in turn). */
  const playChain = (passes = chainListens) => {
    const segs = partsOfKeys(chain);
    saveRepeat();
    updateDeep('repeat', { ayahRepeat: 1, rangeRepeat: 1, gapMode: 'off' });
    waiting.current = { kind: 'chain', segs, i: 0, pass: 0, passes, done: usePlayer.getState().rangeDone };
    setChainPass(0);
    setPhase('chain');
    void usePlayer.getState().play(segs[0].surah, { fromAyah: segs[0].from, toAyah: segs[0].to });
  };
  /** Your turn: about as long as the reciter took, and a little more — then on. */
  const yourTurn = (p: 'recite' | 'reciteChain', ks: string[]) => {
    const secs = Math.max(p === 'recite' ? 4 : 6, Math.ceil(ks.reduce((a, x) => a + ayahSeconds(x), 0) * 1.3));
    waiting.current = null;
    setTotal(secs); setLeft(secs); setPhase(p);
  };
  /** After your turn: the chain (from the second āyah, if you hear it), then the test. */
  const afterRecite = () => { if (k > 0 && chainListens > 0) playChain(); else setPhase('test'); };

  /* Opened by a tap: start the first āyah straight away. A layout effect runs within that tap's
     update, so iPhone still counts it as started by the user and lets the audio play. */
  useLayoutEffect(() => { listen(keys[0], own[keys[0]] ?? base); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The player finished what we asked for.
  useEffect(() => {
    const w = waiting.current;
    if (!w || player.rangeDone <= w.done) return;
    if (w.kind === 'ayah') {
      if (phase === 'listen' && w.key === key) { haptic(30); yourTurn('recite', [key]); }
      return;
    }
    if (phase !== 'chain') return;
    // next part of the chain, or the next time through, or your turn
    let { i, pass } = w;
    i++;
    if (i >= w.segs.length) { i = 0; pass++; }
    if (pass >= w.passes) { haptic(30); yourTurn('reciteChain', chain); return; }
    waiting.current = { ...w, i, pass, done: player.rangeDone };
    setChainPass(pass);
    const seg = w.segs[i];
    void usePlayer.getState().play(seg.surah, { fromAyah: seg.from, toAyah: seg.to });
  }, [player.rangeDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // The "your turn" countdown.
  useEffect(() => {
    if (phase !== 'recite' && phase !== 'reciteChain') return;
    if (left <= 0) { if (phase === 'recite') afterRecite(); else setPhase('test'); return; }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, left]); // eslint-disable-line react-hooks/exhaustive-deps

  const setListensFor = (n: number) => { const next = { ...own, [key]: Math.max(1, Math.min(20, n)) }; setOwn(next); writeOwn(next); };
  const struggling = () => { const n = listens + 1; setListensFor(n); listen(key, n); };
  const gotIt = () => {
    haptic(20);
    onLearnt(key);   // reciting it from memory, in the chain, ticks it off
    if (k + 1 < keys.length) { const nk = keys[k + 1]; setK(k + 1); listen(nk, own[nk] ?? base); }
    else { setPhase('whole'); usePlayer.getState().pause(); }
  };
  const stop = () => { usePlayer.getState().pause(); waiting.current = null; };

  return (
    <div className="space-y-3" data-testid="hifz-steps" data-phase={phase}>
      <div className="flex items-center justify-between px-1">
        <span className="text-[13px] font-semibold uppercase tracking-wide text-muted">Step by step · āyah {Math.min(k + 1, keys.length)} of {keys.length}</span>
        <button type="button" onClick={() => { stop(); onExit(); }} className="text-[13px] font-semibold text-accent">Leave step by step</button>
      </div>
      <div className="flex gap-1" aria-hidden>{keys.map((x, i) => <span key={x} className={`h-1.5 flex-1 rounded-full ${i < k || phase === 'whole' ? 'bg-[var(--win-ok)]' : i === k ? 'bg-accent' : 'bg-fill'}`} />)}</div>

      {phase === 'listen' && (
        <Card className="space-y-3 p-4">
          <div className="text-[16px] font-bold">Listen · {label([key])}</div>
          <p className="text-[13px] leading-snug text-muted">Hear it {listens} {listens === 1 ? 'time' : 'times'}, repeating it in each pause. Then it’s your turn to recite it{k && chainListens ? `, then you’ll hear āyāt 1–${k + 1} together` : ''}.</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => (hearing ? player.pause() : listen(key, listens))} className="press flex flex-1 items-center justify-center gap-2 rounded-2xl bg-accent py-3 text-[16px] font-semibold text-accent-ink">
              {hearing ? <><Pause size={18} /> {player.status === 'gap' ? 'Your turn — repeat it' : `Listening ${Math.min(player.ayahPass + 1, listens)} of ${listens}`}</> : <><Headphones size={18} /> {waiting.current ? 'Carry on listening' : 'Start listening'}</>}
            </button>
            <button type="button" onClick={() => { stop(); yourTurn('recite', [key]); }} className="press rounded-2xl bg-fill px-4 py-3 text-[14px] font-semibold">Skip</button>
          </div>
          <ListensRow label="Listens for this āyah" value={listens} onChange={setListensFor} />
        </Card>
      )}

      {(phase === 'recite' || phase === 'reciteChain') && (
        <div data-testid="hifz-your-turn"><Card className="space-y-3 p-4">
          <div className="text-[16px] font-bold">Your turn · recite {phase === 'recite' ? label([key]) : `āyāt 1–${k + 1} together`}</div>
          <p className="text-[13px] leading-snug text-muted">{phase === 'recite' ? 'Say it yourself, as you heard it.' : 'All of them, in one go, as you just heard them.'} Then {phase === 'recite' && k > 0 && chainListens > 0 ? 'you’ll hear the chain' : 'you’ll recite from memory'}.</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-fill"><div className="h-full rounded-full bg-accent transition-[width] duration-1000 ease-linear" style={{ width: `${(left / total) * 100}%` }} /></div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => (phase === 'recite' ? afterRecite() : setPhase('test'))} className="press flex flex-1 items-center justify-center gap-2 rounded-2xl bg-accent py-3 text-[16px] font-semibold text-accent-ink">
              <Check size={18} /> Ready <span className="tabular-nums opacity-80">· {left}s</span>
            </button>
            <button type="button" onClick={() => setLeft((n) => n + 10)} className="press rounded-2xl bg-fill px-3 py-3 text-[14px] font-semibold">+10s</button>
            <button type="button" onClick={() => (phase === 'recite' ? listen(key, listens) : playChain())} className="press rounded-2xl bg-fill px-3 py-3 text-[14px] font-semibold">Hear again</button>
          </div>
        </Card></div>
      )}

      {phase === 'chain' && (
        <Card className="space-y-3 p-4">
          <div className="text-[16px] font-bold">Hear them together · āyāt 1–{k + 1}</div>
          <p className="text-[13px] leading-snug text-muted">The chain so far, straight through{chainListens > 1 ? ` — ${Math.min(chainPass + 1, chainListens)} of ${chainListens}` : ''}. Recite along quietly; then it’s your turn.</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => (busy ? player.pause() : playChain())} className="press flex flex-1 items-center justify-center gap-2 rounded-2xl bg-accent py-3 text-[16px] font-semibold text-accent-ink">
              {busy ? <><Pause size={18} /> Listening</> : <><Headphones size={18} /> Play the chain</>}
            </button>
            <button type="button" onClick={() => { stop(); yourTurn('reciteChain', chain); }} className="press rounded-2xl bg-fill px-4 py-3 text-[14px] font-semibold">Skip</button>
          </div>
          <ListensRow label="Times to hear the chain" value={chainListens} min={0} onChange={(n) => update({ hifzChainListens: Math.max(0, Math.min(10, n)) })} />
        </Card>
      )}

      {phase === 'test' && (
        <Card className="space-y-3 p-4">
          <div className="text-[16px] font-bold">Recite from memory · {k ? `āyāt 1–${k + 1}` : label([key])}</div>
          <p className="text-[13px] leading-snug text-muted">{k ? 'The whole chain so far, without looking — then tap each āyah to check it.' : 'Without looking — then tap the āyah to check it.'}</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={struggling} className="press rounded-2xl bg-[var(--win-warn)]/15 px-3 py-3 text-[14px] font-semibold text-[var(--win-warn)]">Struggling — hear it again (+1)</button>
            <button type="button" onClick={gotIt} className="press rounded-2xl bg-[var(--win-ok)]/15 px-3 py-3 text-[14px] font-semibold text-[var(--win-ok)]">{k + 1 < keys.length ? 'Got it — next āyah' : 'Got it — the whole lesson'}</button>
          </div>
        </Card>
      )}

      {phase === 'whole' && (
        <Card className="p-4">
          <div className="text-[16px] font-bold">Now the whole lesson</div>
          <p className="mt-1 text-[13px] leading-snug text-muted">Recite all {keys.length} āyāt from memory and check each one — tick off every āyah you know. Untick one that slipped, and save the rest.</p>
        </Card>
      )}

      {k >= 2 && (phase === 'listen' || phase === 'test') && (
        <div className="rounded-2xl bg-fill px-3 py-2">
          <ListensRow label="Listens for each new āyah" value={base} onChange={(n) => update({ hifzListens: Math.max(1, Math.min(20, n)) })} />
          <ListensRow label="Times to hear the chain" value={chainListens} min={0} onChange={(n) => update({ hifzChainListens: Math.max(0, Math.min(10, n)) })} />
          <p className="mt-1 text-[12px] text-muted">The chain is growing — lower these if the āyāt are coming quicker.</p>
        </div>
      )}

      <TajweedSwitch />
      {phase === 'listen' || phase === 'recite'
        ? <AyahList key={`l-${key}`} parts={partsOfKeys([key])} hidden={false} />
        : phase === 'chain' || phase === 'reciteChain'
          ? <AyahList key={`c-${k}`} parts={partsOfKeys(chain)} hidden={false} />
          : <AyahList key={`t-${phase}-${k}`} parts={partsOfKeys(phase === 'whole' ? keys : chain)} hidden marks={marks} />}
    </div>
  );
}

type StepPhase = 'listen' | 'recite' | 'chain' | 'reciteChain' | 'test' | 'whole';

/** How long the reciter takes over an āyah (from the timings of what's playing), or a fair guess. */
function ayahSeconds(key: string): number {
  const seg = usePlayer.getState().track?.ayahs.find((a) => a.verseKey === key);
  return seg && seg.end > seg.start ? (seg.end - seg.start) / 1000 : 6;
}

function ListensRow({ label, value, onChange, min = 1 }: { label: string; value: number; onChange: (n: number) => void; min?: number }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[14px] font-medium">{label}</span>
      <span className="flex items-center gap-1 rounded-full bg-surface px-1.5 py-1">
        <button type="button" aria-label={`Fewer: ${label}`} onClick={() => onChange(value - 1)} disabled={value <= min} className="press grid h-7 w-7 place-items-center rounded-full disabled:opacity-30"><Minus size={14} /></button>
        <span className="min-w-[44px] text-center text-[14px] font-bold tabular-nums" aria-label={label}>{value ? `${value}×` : 'Off'}</span>
        <button type="button" aria-label={`More: ${label}`} onClick={() => onChange(value + 1)} disabled={value >= 20} className="press grid h-7 w-7 place-items-center rounded-full disabled:opacity-30"><Plus size={14} /></button>
      </span>
    </div>
  );
}

/** The tajweed colours switch and colour key, on their own. */
function TajweedSwitch() {
  const on = useSettings((s) => s.settings.hifzTajweed !== false);
  const update = useSettings((s) => s.update);
  const [legend, setLegend] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2 px-1">
      <button type="button" role="switch" aria-checked={on} onClick={() => update({ hifzTajweed: !on })}
        className={`press flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${on ? 'bg-gradient-to-r from-[var(--tj-ghunnah)]/25 to-[var(--tj-madd246)]/25' : 'bg-fill text-muted'}`}>
        <Palette size={14} />Tajweed colours {on ? 'on' : 'off'}
      </button>
      {on && <button type="button" onClick={() => setLegend(true)} className="text-[13px] font-semibold text-accent">Colour key</button>}
      <TajweedLegend open={legend} onClose={() => setLegend(false)} />
    </div>
  );
}

function HideToggle({ hidden, onChange }: { hidden: boolean; onChange: (v: boolean) => void }) {
  const on = useSettings((s) => s.settings.hifzTajweed !== false);
  const update = useSettings((s) => s.update);
  const [legend, setLegend] = useState(false);
  return (
    <div className="space-y-2">
      <Toggle hidden={hidden} onChange={onChange} />
      <div className="flex items-center justify-between gap-2 px-1">
        <button type="button" role="switch" aria-checked={on} onClick={() => update({ hifzTajweed: !on })}
          className={`press flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${on ? 'bg-gradient-to-r from-[var(--tj-ghunnah)]/25 to-[var(--tj-madd246)]/25' : 'bg-fill text-muted'}`}>
          <Palette size={14} />Tajweed colours {on ? 'on' : 'off'}
        </button>
        {on && <button type="button" onClick={() => setLegend(true)} className="text-[13px] font-semibold text-accent">Colour key</button>}
      </div>
      <TajweedLegend open={legend} onClose={() => setLegend(false)} />
    </div>
  );
}

function Toggle({ hidden, onChange }: { hidden: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex rounded-full bg-fill p-1" role="radiogroup" aria-label="Show the text">
      {([[false, 'Read', Eye], [true, 'Test myself', EyeOff]] as const).map(([v, label, Icon]) => (
        <button key={label} type="button" role="radio" aria-checked={hidden === v} onClick={() => onChange(v)}
          className={`press flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-[14px] font-semibold ${hidden === v ? 'bg-surface shadow' : 'text-muted'}`}><Icon size={15} />{label}</button>
      ))}
    </div>
  );
}

/* ── Revising a page ──────────────────────────────────────────────── */
const GRADES: { g: Grade; label: string; hint: string; cls: string }[] = [
  { g: 'forgot', label: 'Forgot', hint: 'Back to daily for a week', cls: 'bg-[var(--win-crit)]/15 text-[var(--win-crit)]' },
  { g: 'shaky', label: 'Some mistakes', hint: 'Sooner', cls: 'bg-[var(--win-warn)]/15 text-[var(--win-warn)]' },
  { g: 'strong', label: 'Strong', hint: 'Later', cls: 'bg-[var(--win-ok)]/15 text-[var(--win-ok)]' },
];

export function HifzReview() {
  const { page: param } = useParams();
  const page = Number(param);
  const plan = usePlan();
  const today = useToday();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const pages = useProgress((s) => s.hifzPages ?? NO_PAGES);
  const gradeIt = useProgress((s) => s.gradeHifzPage);
  const [hidden, setHidden] = useState(sp.get('show') !== '1');   // revise from memory first
  useRestoreRepeat();
  const p = pages[String(page)];
  if (!plan || !p || p.deleted) return <div className="page-pad px-6 pt-20 text-center"><p className="text-muted">This page isn’t in your revision.</p><Link to="/hifz" className="font-semibold text-accent">Back to Hifz</Link></div>;
  const parts = pageParts(plan, page);
  const done = (g: Grade) => {
    haptic(g === 'strong' ? 30 : 15);
    gradeIt(page, g, today);
    // On to the next page waiting today, in the order of the list.
    const q = reviewQueue({ ...pages, [p.id]: { ...p, last: { day: today, grade: g } } }, today, plan.maxReview);
    const next = [...q.recent, ...q.due][0];
    nav(next ? `/hifz/review/${next.page}` : '/hifz', { replace: true });
  };

  return (
    <div className="page-pad">
      <LargeTitle back="/hifz" title={`Page ${page}`} subtitle={`${parts.length ? partsLabel(parts) : ''}${isRecent(p, today) ? ' · learnt this week' : ''}`} />
      <div className="space-y-4 px-4 pb-48">
        <p className="px-1 text-[14px] leading-snug text-muted">Recite the page from memory first — the first word of each āyah is your prompt — then tap each āyah to check yourself. Be honest: a page you mark weak simply comes back sooner.</p>
        <div className="flex flex-wrap gap-2">{parts.map((pt) => <ListenButton key={`${pt.surah}:${pt.from}`} part={pt} label={parts.length > 1 ? `Listen · ${chapter(pt.surah).n}` : 'Listen'} />)}</div>
        <HideToggle hidden={hidden} onChange={setHidden} />
        <AyahList key={page} parts={parts} hidden={hidden} />
      </div>
      <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[calc(var(--safe-bottom)+76px)] pt-2">
        <div className="mx-auto max-w-[680px] rounded-2xl bg-surface p-2 shadow-lg">
          <div className="px-2 pb-1.5 text-center text-[13px] font-semibold text-muted">How did it go?</div>
          <div className="grid grid-cols-3 gap-2">
            {GRADES.map((x) => (
              <button key={x.g} type="button" onClick={() => done(x.g)} className={`press rounded-xl px-2 py-2.5 text-center ${x.cls}`}>
                <span className="block text-[15px] font-bold">{x.label}</span><span className="block text-[11px] opacity-80">{x.hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Guide ────────────────────────────────────────────────────────── */
const METHODS: { name: string; where: string; body: string[] }[] = [
  {
    name: 'Sabaq, sabqi, manzil',
    where: 'Taught in madrasas across South Asia, and in pesantren in Indonesia and Malaysia',
    body: [
      'Every day has three parts. Sabaq: the new lesson. Sabqi: the recent lessons, revised daily so they settle. Manzil: older portions, revised in a cycle so nothing is lost.',
      'Revision is never skipped for new lessons — on a hard day, do less new and keep the revision. This app plans your day this way.',
    ],
  },
  {
    name: 'The Ottoman (Turkish) page method',
    where: 'Used in Turkey, the Balkans and beyond',
    body: [
      'Memorise one page of the same muṣḥaf print at a time, so the page itself becomes part of the memory — where each āyah sits, how the page ends.',
      'A page is often learnt in thirds, five lines at a time. Some schools take one page from every juz in turn (“rounds”), so the whole Qurʾān is being revised from the start.',
    ],
  },
  {
    name: 'The Mauritanian lawḥ',
    where: 'The maḥāḍir of Mauritania and West Africa',
    body: [
      'The student writes the lesson on a wooden board (lawḥ) and recites it from the board many times — often a hundred or more — before reciting it from memory.',
      'A lesson isn’t left until it is perfect, and the previous days’ lessons are recited again, fewer times each day. Few lessons, very strongly learnt.',
    ],
  },
  {
    name: 'Spaced revision',
    where: 'How memory works, and how this app schedules your manzil',
    body: [
      'Memory fades unless it is revisited, and each revision lasts longer than the one before. So a page you knew well comes back after a longer gap, and a page you stumbled on comes back sooner.',
      'Here a page is revised daily for its first week, then after a few days, then a week, two weeks, up to a month. Mark a page “forgot” and it returns to daily revision.',
    ],
  },
];

const TIPS = [
  'Keep one muṣḥaf — the same print and page layout (this app uses the Madinah page) — so the pages stay in your mind’s eye.',
  'Choose a fixed time: after Fajr is the time most teachers recommend, when the mind is fresh.',
  'Small and every day beats a lot now and then. A lesson you keep is better than three you lose.',
  'Learn with tajweed from the start — the lesson and revision screens colour each rule (tap Colour key for what they mean) — and recite to a teacher or a friend who can correct you — the Qurʾān is passed on by hearing it (talaqqī).',
  'Pray with what you learn, especially at night; revise in your sunnah and nafl prayers.',
  'Understand what you memorise: read the meaning of the lesson before you begin.',
];

export function HifzGuide() {
  return (
    <div className="page-pad">
      <LargeTitle back="/hifz" title="How to memorise" ar="طرق الحفظ" />
      <div className="space-y-5 px-4 pb-10">
        <AyatCard keys={[HIFZ.ayat[0]]} />
        <Heading>Passages to memorise first</Heading>
        <p className="-mt-3 px-1 text-[14px] leading-snug text-muted">The Sunnah singles these out — for protection, for the night, for the prayer. With “Start with the passages the Sunnah singles out” on, your plan begins with them.</p>
        <div className="space-y-3" data-testid="hifz-passages">
          {PASSAGES.map((p) => (
            <Card key={p.id} className="p-4">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[16px] font-bold">{p.title}</span>
                <span className="shrink-0 text-[12px] font-semibold tabular-nums text-gold">{p.ranges.map(([s, a, b]) => (a === 1 && b === CHAPTERS[s - 1].v ? `Sūrah ${s}` : `${s}:${a}${b > a ? `–${b}` : ''}`)).join(', ')}</span>
              </div>
              <p className="mt-0.5 text-[14px] text-muted">{p.why}</p>
              <div className="mt-3 space-y-2">{(HIFZ.passages[p.id] ?? []).map((id) => <HadithCard key={id} id={id} />)}</div>
            </Card>
          ))}
        </div>
        <Heading>The daily tracks</Heading>
        <Tracks />
        <Heading>Methods ḥuffāẓ use</Heading>
          <div className="space-y-3">
            {METHODS.map((m) => (
              <Card key={m.name} className="p-4">
                <div className="text-[16px] font-bold">{m.name}</div>
                <div className="text-[12px] font-semibold text-gold">{m.where}</div>
                {m.body.map((b) => <p key={b} className="mt-2 text-[14px] leading-relaxed text-muted">{b}</p>)}
              </Card>
            ))}
          </div>
        <Heading>Good habits</Heading>
        <Card className="p-4"><ul className="space-y-2">{TIPS.map((t) => <li key={t} className="flex gap-2 text-[14px] leading-snug"><Check size={16} className="mt-0.5 shrink-0 text-accent" />{t}</li>)}</ul></Card>
        <Heading>Its virtue</Heading>
        <div className="space-y-3">{HIFZ.virtue.map((id) => <HadithCard key={id} id={id} />)}</div>
        <Heading>Keeping it</Heading>
        <div className="space-y-3">{HIFZ.keep.map((id) => <HadithCard key={id} id={id} />)}<AyatCard keys={[HIFZ.ayat[1]]} /></div>
        <p className="px-2 text-[12px] leading-snug text-muted">{VIRTUES_CREDIT} The methods are long-standing teaching traditions, described in our own words.</p>
      </div>
    </div>
  );
}

/* ── Setup ────────────────────────────────────────────────────────── */
const ORDERS: { value: HifzOrder; label: string; note: string }[] = [
  { value: 'end', label: 'From the end', note: 'An-Nās back to al-Fātiḥah, surah by surah — short surahs first' },
  { value: 'amma-first', label: 'Juz ʿAmma first', note: 'Then from al-Baqarah onward to the end' },
  { value: 'start', label: 'From the beginning', note: 'Al-Fātiḥah, al-Baqarah… to an-Nās' },
];
const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const QUICK_KNOWN: { label: string; surahs: number[] }[] = [
  { label: 'Al-Fātiḥah', surahs: [1] },
  { label: 'Juz ʿAmma', surahs: Array.from({ length: 37 }, (_, i) => 78 + i) },
  { label: 'Juz Tabārak', surahs: Array.from({ length: 11 }, (_, i) => 67 + i) },
  { label: 'Yā-Sīn', surahs: [36] },
  { label: 'Al-Kahf', surahs: [18] },
  { label: 'Al-Mulk', surahs: [67] },
];
const PACES = [0.25, 0.5, 1, 2];

function SetupSheet({ open, onClose, plan }: { open: boolean; onClose: () => void; plan: HifzPlan | null }) {
  const today = useToday();
  const save = useProgress((s) => s.saveHifz);
  const reset = useProgress((s) => s.resetHifz);
  const initialYears = plan?.targetDay ? Math.max(1, Math.round((Date.parse(plan.targetDay) - Date.parse(plan.startDay)) / (365.25 * 864e5))) : plan ? 0 : 3;
  const [years, setYears] = useState<number>(initialYears);     // 0 = own pace
  const [pace, setPace] = useState(plan?.pace ?? 0.5);
  const [order, setOrder] = useState<HifzOrder>(plan?.order ?? 'end');
  const [sunnahFirst, setSunnahFirst] = useState(plan ? !!plan.sunnahFirst : true);
  const started = !!plan && plan.position > 0;
  const [days, setDays] = useState<number[]>(plan?.studyDays ?? [0, 1, 2, 3, 4, 6]);
  const [known, setKnown] = useState<number[]>(plan?.known ?? [1]);
  const [maxReview, setMaxReview] = useState(plan?.maxReview ?? 20);
  const [picking, setPicking] = useState(false);
  const goalChanged = !plan || years !== initialYears;
  const target = years ? (goalChanged ? targetFor(today, years) : plan!.targetDay) : null;

  const preview: HifzPlan = { id: 'main', order, sunnahFirst, startDay: today, targetDay: target, pace, studyDays: days, known, position: plan?.position ?? 0, maxReview, createdAt: 0, updatedAt: 0 };
  const perDay = days.length ? dailyPages(preview, today) : 0;
  const toggle = (list: number[], n: number) => (list.includes(n) ? list.filter((x) => x !== n) : [...list, n].sort((a, b) => a - b));
  const quickOn = (s: number[]) => s.every((x) => known.includes(x));

  return (
    <Sheet open={open} onClose={onClose} title={plan ? 'Your hifz plan' : 'Make your hifz plan'}>
      <div className="space-y-5 px-5 pb-8">
        <div>
          <div className="pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">Finish memorising in</div>
          <div className="flex flex-wrap gap-2">
            {GOAL_YEARS.map((y) => <Chip key={y} on={years === y} onClick={() => setYears(y)}>{y} {y === 1 ? 'year' : 'years'}</Chip>)}
            <Chip on={years === 0} onClick={() => setYears(0)}>My own pace</Chip>
          </div>
          {years === 0 && (
            <div className="mt-3 flex flex-wrap gap-2">{PACES.map((p) => <Chip key={p} on={pace === p} onClick={() => setPace(p)}>{p === 0.25 ? '¼ page' : p === 0.5 ? '½ page' : `${p} ${p === 1 ? 'page' : 'pages'}`} a day</Chip>)}</div>
          )}
        </div>

        <div>
          <div className="pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">Days with a new lesson</div>
          <div className="flex justify-between gap-1">
            {DAYS.map((d, i) => <button key={i} type="button" aria-label={DAY_NAMES[i]} aria-pressed={days.includes(i)} onClick={() => setDays(toggle(days, i))} className={`press h-10 flex-1 rounded-xl text-[14px] font-bold ${days.includes(i) ? 'bg-accent text-accent-ink' : 'bg-fill text-muted'}`}>{d}</button>)}
          </div>
          <p className="mt-1.5 text-[12px] text-muted">Revision is every day. Many keep one day a week (often Friday) for revision only.</p>
        </div>

        <div>
          <div className="pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">Order</div>
          <button type="button" role="switch" aria-checked={sunnahFirst} disabled={started} onClick={() => setSunnahFirst((v) => !v)}
            className={`press mb-2 flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left disabled:opacity-60 ${sunnahFirst ? 'bg-gold/15 shadow-[inset_0_0_0_1.5px_var(--gold)]' : 'bg-fill'}`}>
            <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md ${sunnahFirst ? 'bg-gold text-white' : 'border-2 border-hairline'}`}>{sunnahFirst && <Check size={13} strokeWidth={3} />}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold">Start with the passages the Sunnah singles out</span>
              <span className="block text-[12px] leading-snug text-muted">{PASSAGES.map((p) => p.title).join(' · ')} — then the rest in the order below</span>
            </span>
          </button>
          <div className="space-y-2">
            {ORDERS.map((o) => (
              <button key={o.value} type="button" disabled={started && o.value !== plan!.order} onClick={() => setOrder(o.value)}
                className={`press flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left disabled:opacity-40 ${order === o.value ? 'bg-accent/15 shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'bg-fill'}`}>
                <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold">{o.label}</span><span className="block text-[12px] text-muted">{o.note}</span></span>
                {order === o.value && <Check size={18} className="text-accent" />}
              </button>
            ))}
          </div>
          {started && <p className="mt-1.5 text-[12px] text-muted">The order is fixed once you’ve started, so your progress stays in place.</p>}
        </div>

        <div>
          <div className="pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">Already memorised</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_KNOWN.map((k) => <Chip key={k.label} on={quickOn(k.surahs)} onClick={() => setKnown(quickOn(k.surahs) ? known.filter((x) => !k.surahs.includes(x)) : [...new Set([...known, ...k.surahs])].sort((a, b) => a - b))}>{k.label}</Chip>)}
            <Chip on={false} onClick={() => setPicking(true)}>Choose surahs… {known.length ? `(${known.length})` : ''}</Chip>
          </div>
          <p className="mt-1.5 text-[12px] text-muted">These are left out of your lessons and go straight into revision.</p>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-fill px-3 py-2">
          <span className="text-[14px]"><span className="block font-semibold">Older revision a day</span><span className="block text-[12px] text-muted">at most, in pages</span></span>
          <Stepper value={maxReview} min={5} max={60} step={5} onChange={setMaxReview} />
        </div>

        <div className="rounded-2xl bg-gold/10 p-4 text-center" data-testid="hifz-summary">
          {days.length === 0 ? <div className="text-[14px] font-semibold text-[var(--win-warn)]">Choose at least one day for new lessons.</div> : (
            <>
              <div className="text-[20px] font-bold">≈ {linesOf(perDay)} lines a day</div>
              <div className="text-[13px] text-muted">{fmtPages(perDay)} {perDay === 1 ? 'page' : 'pages'} · {days.length} {days.length === 1 ? 'day' : 'days'} a week{target ? ` · finish by ${fmtDate(target)}` : ''}</div>
              {perDay > 2 && <div className="mt-1 text-[12px] font-semibold text-[var(--win-warn)]">That’s a lot each day — a longer goal leaves room for revision.</div>}
            </>
          )}
        </div>

        <button type="button" disabled={days.length === 0} onClick={() => { save({ order, sunnahFirst, startDay: plan?.startDay ?? today, targetDay: target, pace, studyDays: days, known, maxReview }, today); onClose(); }}
          className="press w-full rounded-2xl bg-accent py-3.5 text-[16px] font-semibold text-accent-ink disabled:opacity-40">{plan ? 'Save' : 'Begin, bi-idhnillāh'}</button>
        {plan && <button type="button" onClick={() => { if (confirm('Delete your hifz plan and its revision history? This can’t be undone.')) { reset(); onClose(); } }} className="w-full text-center text-[14px] font-semibold text-danger">Delete plan</button>}
      </div>

      <Sheet open={picking} onClose={() => setPicking(false)} title="Surahs you know">
        <ul className="px-4 pb-6">
          {CHAPTERS.map((c) => (
            <li key={c.id}>
              <button type="button" aria-pressed={known.includes(c.id)} onClick={() => setKnown(toggle(known, c.id))} className="press flex w-full items-center gap-3 border-b border-hairline py-2.5 text-left">
                <span className={`grid h-6 w-6 place-items-center rounded-md ${known.includes(c.id) ? 'bg-accent text-accent-ink' : 'border-2 border-hairline'}`}>{known.includes(c.id) && <Check size={14} strokeWidth={3} />}</span>
                <span className="w-8 text-[13px] tabular-nums text-muted">{c.id}</span>
                <span className="flex-1 text-[15px]">{c.n}</span>
                <span className="font-arabic text-[18px] text-gold" dir="rtl">{c.ar}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    </Sheet>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h2 className="flex items-center gap-1.5 px-1 pt-1 text-[13px] font-semibold uppercase tracking-wide text-muted"><StarIcon size={11} strokeWidth={2} className="text-gold" />{children}</h2>;
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return <button type="button" aria-pressed={on} onClick={onClick} className={`press rounded-full px-3.5 py-1.5 text-[14px] font-semibold ${on ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>{children}</button>;
}
